import React from 'react';

import { Box, Chip, InputAdornment, InputBase, Typography, AutocompleteRenderInputParams, AutocompleteRenderOptionState } from '@mui/material';
import { makeStyles, withStyles } from 'tss-react/mui';
import { Search } from '@mui/icons-material';
import { useCurrentuser } from '@uplift/contexts';
import { UserRole } from '@uplift/types';
import { isHigherEdAdmin } from '@uplift/utils';
import { AsyncAutocompleteRaw, AutocompleteHighlighter } from '../../../Inputs';
import { useUserSearch } from './user-search.hook';

export const StyledClientsSearchInputBase = withStyles(InputBase, (theme) => ({
  root: {
    backgroundColor: theme.palette.grey[100],
    display: 'flex',
    paddingLeft: 8,
    paddingTop: 3,
    paddingBottom: 3,
    borderRadius: 4,
    boxShadow: `0px 0px 0px 0px ${theme.palette.secondary.main} inset`,
    transition: 'box-shadow 100ms',
  },
  focused: {
    transition: 'box-shadow 50ms',
    boxShadow: `0px 0px 0px 2px ${theme.palette.secondary.main} inset`
  },
}));

const useStyles = makeStyles<Partial<AdminUserSearchProps>>()({
  listbox: {
    maxHeight: '220px',
  },
});

export type UserSearch = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  preferredName: string;
  active: boolean;
  adminRole: string | null;
  role: UserRole;
};

const NameHighlighter = withStyles(AutocompleteHighlighter, theme => ({
  highlight: {
    fontWeight: 600,
    color: theme.palette.secondary.main
  }
}));

export const userAutocompleteRenderItem = (option: UserSearch, state: AutocompleteRenderOptionState) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" flex="1">
    <Typography>
      <NameHighlighter
        text={`${option.firstName}${option.preferredName ? ' (' + option.preferredName + ')' : ''} ${option.lastName}`}
        highlight={state.inputValue}
      />
    </Typography>
    {!option.active && <Chip label="Inactive" variant="filled" color="default" size="small" />}
  </Box>
);

const CustomAutocompleteInput = (params: AutocompleteRenderInputParams) => {
  const { ref: inputRef, classes: _classes, ...inputProps } = params.inputProps as any;

  return (
    <div ref={params.InputProps.ref}>
      <StyledClientsSearchInputBase
        {...inputProps}
        endAdornment={params.InputProps.endAdornment}
        inputRef={inputRef}
        fullWidth
        placeholder="Search users"
        startAdornment={<InputAdornment position="start"><Search color="disabled" /></InputAdornment>}
      />
    </div>
  );
};

export type AdminUserSearchProps = {
  onUserSelect: (user: UserSearch) => void;
  customAutocompleteInput?: (params: AutocompleteRenderInputParams) => React.ReactElement;
};

export const AdminUserSearch: React.FC<AdminUserSearchProps> = (props) => {
  const { classes } = useStyles(props, { props });
  const me = useCurrentuser();
  const isHigherEdAdminRole = isHigherEdAdmin(me);
  const searchUsers = useUserSearch(isHigherEdAdminRole);
  const renderInput = props.customAutocompleteInput || CustomAutocompleteInput;
  const [ key, setKey ] = React.useState(0);

  return (
    <AsyncAutocompleteRaw
      key={key}
      forcePopupIcon={false}
      fullWidth
      blurOnSelect
      clearOnBlur
      disablePortal
      openWithNoTerm={false}
      noOptionsText="No results"
      name="user-search"
      classes={classes}
      renderInput={renderInput}
      renderOption={userAutocompleteRenderItem}
      setValue={(user: UserSearch) => {
        props.onUserSelect(user);
        setKey(Date.now());
      }}
      asyncOptions={async (term) => await searchUsers(term)}
      data-testid="admin-user-search"
    />
  );
};
